/**
 * Eén GA4-identiteit per product, van bekijken tot aankoop.
 *
 * GA4 legt "bekeken", "in winkelwagen" en "gekocht" in zijn productrapporten naast elkaar op
 * `item_id` / `item_name`. Voor glas op maat verschilde die per stap: bekeken en in de winkelwagen
 * heette een ruit "Gehard glas", in de winkelwagen- en afrekenstap "glass" met de maten in de naam,
 * en bij de aankoop (serverzijdig) het volledige variantlabel. Isolatieglas werd bekeken als
 * "isolatieglas" en gekocht als "IQ1006+". Daardoor ontmoetten bekijken en kopen elkaar nooit: in
 * GA4 leek glas nooit verkocht te worden en stonden er alleen gereedschappen bovenaan (13-09-2026).
 *
 * Deze module is de enige bron voor die identiteit — de browser (track.ts, winkelwagen) én de
 * server (createOrder → ga4.ts) gebruiken hem, zodat ze niet opnieuw uit elkaar kunnen lopen.
 *
 *  - artikel:            item_id = basisartikel,    item_name = opgeschoonde artikelnaam
 *  - glas op maat:       item_id = variant-id,      item_name = glassoort ("Gehard glas")
 *  - samengesteld glas:  item_id = soort-sleutel,   item_name = soortnaam ("Isolatieglas op maat")
 *
 * Artikel = het BASISartikel: een voorzetraam of rooster krijgt in de winkelwagen per maat/kleur
 * een eigen slug (`…__1200x800`) en een kleurvariant heeft een eigen slug, terwijl de productpagina
 * het basisartikel bekijkt. En de naam is de opgeschoonde naam (zonder leverancierscode), niet de
 * ruwe Timmers-naam die de productpagina eerst stuurde of de kleur die de winkelwagen erachter zet.
 */
import { cleanArticleName } from "./pricing/overrides";

export const GA_SOORT_NAAM: Record<string, string> = {
  isolatieglas: "Isolatieglas op maat",
  shower: "Douche-opstelling",
  benglas: "Vacuümglas (BENGglas)",
};

/** Slug zonder configuratiesuffix: `voorzetraam-4mm__1200x800` → `voorzetraam-4mm`. */
export function gaBasisSlug(slug: string): string {
  return slug.split("__")[0] || slug;
}

/**
 * Artikel: basisslug + opgeschoonde naam. Geef de naam en het artikelnummer van het BASISartikel
 * mee (kleurvariant opzoeken doet de aanroeper: server via basisArtikelVan, browser via variantThumbs).
 */
export function gaArtikelVelden(
  slug: string,
  naam: string | undefined,
  artikelnummer: string | undefined,
  categorie?: string,
): GaVelden {
  const basis = gaBasisSlug(slug);
  return { gaItemId: basis, gaItemName: naam ? cleanArticleName(naam, artikelnummer) : basis, gaItemCategory: categorie };
}

export interface GaVelden {
  /** GA4 `item_id` — gelijk aan wat de browser bij bekijken en in de winkelwagen stuurt. */
  gaItemId: string;
  /** GA4 `item_name` — de soort, niet de maten (anders wordt elke configuratie een eigen regel). */
  gaItemName: string;
  /**
   * GA4 `item_category`. Werd alleen bij het BEKIJKEN meegestuurd, niet bij in-winkelwagen of bij
   * de aankoop — daardoor stond alle verkoop in GA4 onder "(not set)" en was glas niet van
   * artikelen te scheiden (30-10-2026). Zelfde waarde als view_item: voor een artikel de categorie
   * uit de catalogus, voor glas de productSlug, voor een samengesteld product de soort.
   */
  gaItemCategory?: string;
}

/** Samengesteld product (isolatieglas, douche, BENGglas): één regel per soort in GA4. */
export function gaSoortVelden(soort: string): GaVelden {
  return { gaItemId: soort, gaItemName: GA_SOORT_NAAM[soort] ?? soort, gaItemCategory: soort };
}

/** Glas op maat: de variant als id (dikte/kleur), de glassoort als naam. */
export function gaGlasVelden(
  variantId: string,
  subcategory: string | undefined,
  terugval: string,
  productSlug?: string,
): GaVelden {
  return { gaItemId: variantId, gaItemName: (subcategory ?? "").trim() || terugval, gaItemCategory: productSlug };
}
